import Big from 'big.js';
import { Toast } from 'bootstrap';
import { DateTime } from 'luxon';
import axios from 'smg/axios';
import * as hyejoo from 'smg/customer-api/hyejoo';
import { Session } from 'smg/customer-api/session';
import * as users from 'smg/customer-api/users';
import { HandleRequestError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import { Escape, Nl2br } from 'smg/html';
import Modal from 'smg/modal';
import {
    ModelChangelogEntry,
    QuoteRequest,
    QuoteRequestProjectTask,
    QuoteRequestProjectTaskPriorityID,
    QuoteRequestProjectTaskStatusID,
    QuoteSent,
    UserEvent,
    UserEventUpload,
} from 'smg/models';
import { GetProofURLs } from 'smg/proofs';
import { G } from 'smg/querystring';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import * as uploads from 'smg/uploads';
import { DollarFormat } from 'smg/util';
import { UUID } from 'smg/uuid';
import { Relref } from '.././hugo';
import { PrepayModal } from '../quote-deposit-modal';

declare interface myWindow extends Window {
    readonly cloudFront: string;
    readonly apiRoot: string;
}
declare const window: myWindow;

export default async function main() {
    const $progressBar = document.querySelector('.pb') as HTMLElement;
    const $pbToggleButton = document.querySelector('.pb-vert-toggle') as HTMLElement;
    const $pbLoadingSpinner = document.querySelector('#pb-loading-spinner') as HTMLElement;
    const $pbcanceled = document.querySelector('#pb-canceled-overlay') as HTMLElement;

    const $pbQueue = $progressBar.querySelector('#pb-in-the-queue') as HTMLElement;
    const $pbQueueDate = $pbQueue.querySelector('.pb-milestone-date') as HTMLElement;
    const $pbQueueMobile = $progressBar.querySelector('#pb-in-the-queue-mobile') as HTMLElement;
    const $pbQueueDateMobile = $pbQueueMobile.querySelector('.pb-milestone-date') as HTMLElement;

    const $pbProgress = $progressBar.querySelector('#pb-in-progress') as HTMLElement;
    const $pbProgressDate = $pbProgress.querySelector('.pb-milestone-date') as HTMLElement;
    const $pbProgressMobile = $progressBar.querySelector('#pb-in-progress-mobile') as HTMLElement;
    const $pbProgressDateMobile = $pbProgressMobile.querySelector('.pb-milestone-date') as HTMLElement;

    const $pbArtDone = $progressBar.querySelector('#pb-art-done-waiting-for-pricing') as HTMLElement;
    const $pbArtDoneDate = $pbArtDone.querySelector('.pb-milestone-date') as HTMLElement;
    const $pbArtDoneMobile = $progressBar.querySelector('#pb-art-done-waiting-for-pricing-mobile') as HTMLElement;
    const $pbArtDoneDateMobile = $pbArtDoneMobile.querySelector('.pb-milestone-date') as HTMLElement;

    const $pbSent = $progressBar.querySelector('#pb-sent') as HTMLElement;
    const $pbSentDate = $pbSent.querySelector('.pb-milestone-date') as HTMLElement;
    const $pbSentMobile = $progressBar.querySelector('#pb-sent-mobile') as HTMLElement;
    const $pbSentDateMobile = $pbSentMobile.querySelector('.pb-milestone-date') as HTMLElement;

    const $pbDatesDesktop = [$pbQueueDate, $pbProgressDate, $pbArtDoneDate, $pbSentDate];
    const $pbDatesMobile = [$pbQueueDateMobile, $pbProgressDateMobile, $pbArtDoneDateMobile, $pbSentDateMobile];
    const $pbMilestones = [$pbQueue, $pbProgress, $pbArtDone, $pbSent];
    const $pbMilestonesMobile = [$pbQueueMobile, $pbProgressMobile, $pbArtDoneMobile, $pbSentMobile];

    const $pbPriorityLine = $progressBar.querySelector('#pb-prioritized') as HTMLElement;
    const $pbPriorityDate = $pbPriorityLine.querySelector('#pb-prioritized-date') as HTMLElement;
    const $pbCancelLine = $progressBar.querySelector('#pb-canceled') as HTMLElement;
    const $pbCancelDate = $pbCancelLine.querySelector('#pb-canceled-date') as HTMLElement;
    const $pbCompleteLine = $progressBar.querySelector('#pb-complete') as HTMLElement;
    const $pbCompleteDate = $pbCompleteLine.querySelector('#pb-complete-date') as HTMLElement;

    const $prepayBtn = $progressBar.querySelector('#pb-button-prepay') as HTMLButtonElement;
    const $cancelTaskBtn = $progressBar.querySelector('#pb-button-cancel') as HTMLButtonElement;
    const $pbActionButtonParent = $progressBar.querySelector('#pb-action-button-parent') as HTMLElement;
    const $pbCanceledButtons = $progressBar.querySelector('#pb-canceled-buttons') as HTMLElement;
    const $pbCanceledReopenBtn = $pbCanceledButtons.querySelector('#canceled-reopen-button') as HTMLAnchorElement;
    const $pbCompleteButtons = $progressBar.querySelector('#pb-complete-buttons') as HTMLElement;
    const $pbCompleteViewQuoteBtn = $pbCompleteButtons.querySelector('#view-quote-btn') as HTMLAnchorElement;

    const $loadingSkeletonParent = document.querySelector('#loading-card') as HTMLElement;
    const $loadingSkeleton = document.querySelector('#loading-skeleton') as HTMLTemplateElement;

    const $mainCard = document.querySelector('#main-card') as HTMLElement;

    const $requestChanges = document.querySelector('#request-changes-form-parent') as HTMLElement;
    const $rcForm = $requestChanges.querySelector('#request-changes-form') as HTMLFormElement;
    const $rcLoadingSpinner = $requestChanges.querySelector('#request-changes-loading-spinner') as HTMLElement;
    const $rcUploadParent = $rcForm.querySelector('#request-changes-uploads-parent') as HTMLLabelElement;
    const $rcUploads = $rcForm.querySelector('#request-changes-uploads') as HTMLLabelElement;
    const $rcImageGallery = $rcForm.querySelector('#request-changes-image-gallery') as HTMLElement;
    const $rcUploadInput = $rcUploads.querySelector('input') as HTMLInputElement;
    const $rcUploadButton = $rcUploads.querySelector('#request-changes-browse-files') as HTMLButtonElement;
    const $rcFormTextArea = $rcForm.querySelector('#request-changes') as HTMLTextAreaElement;
    const $rcFormButtons = $rcForm.querySelector('#request-changes-submit') as HTMLElement;
    const $rcSubmitBtn = $rcFormButtons.querySelector('#request-changes-submit-btn') as HTMLButtonElement;

    const $activityOrderToggle = document.querySelector('.activity-order-toggle') as HTMLButtonElement;
    const $activityItems = document.querySelector('#activity-items') as HTMLElement;
    const $thumb = document.querySelector('#upload-thumbnail-template') as HTMLTemplateElement;

    interface activityItem {
        element: HTMLElement;
        userEventID: string | undefined;
        uploads: activityItemUpload[];
        deleted: boolean;
        deleteQueue: activityItemUpload[];
        deletedUploads: activityItemUpload[];
        content: string;
        createdTime: DateTime;
        updatedTime?: DateTime;
        toasts: (Toast | undefined)[];
    }

    interface activityItemUpload {
        userEventUpload: UserEventUpload;
        element: HTMLElement;
    }

    interface qrptPostResponse {
        id: string;
        userEvent: UserEvent;
    }

    interface uePatchResponse {
        userEvent: UserEvent;
    }

    interface uploadParams {
        Content?: string;
        UploadIDs?: number[];
        user: users.UserDetails;
    }

    let changelogCancelDate: DateTime | undefined;
    let changelogPrepayDate: DateTime | undefined;
    let changelogSentDate: DateTime | undefined;

    $loadingSkeletonParent.append(template.Clone($loadingSkeleton));

    $pbToggleButton.addEventListener('click', e => {
        e.preventDefault();
        if ($progressBar.classList.contains('pb-vert')) {
            $progressBar.classList.remove('pb-vert');
        } else {
            $progressBar.classList.add('pb-vert');
        }
    });

    $activityOrderToggle.addEventListener('click', e => {
        e.preventDefault();
        if ($activityItems.classList.contains('flex-column')) {
            $activityItems.classList.remove('flex-column');
            $activityItems.classList.add('flex-column-reverse');
            $activityOrderToggle.innerHTML = 'Oldest <i class="fas fa-long-arrow-alt-down"></i>';
        } else {
            $activityItems.classList.remove('flex-column-reverse');
            $activityItems.classList.add('flex-column');
            $activityOrderToggle.innerHTML = 'Newest <i class="fas fa-long-arrow-alt-up"></i>';
        }
    });

    const keyFromQuery = G().Get('qrpt');
    if (!keyFromQuery) {
        notFound();
        HandleRequestError('Failed to find matching design ticket');
        return;
    }
    const key = keyFromQuery as string;

    let q: string;
    if (key.length === 43) {
        q = `key:${key}`;
    } else {
        q = `id:${key}`;
    }

    // ========== Session ==============================

    const sess = await Session().catch(() => undefined);
    let sessUser: users.UserDetails | null = null;

    if (sess) {
        sessUser = {
            firstName: sess.firstName,
            lastName: sess.lastName,
            company: sess.company,
            email: sess.email,
            phone: sess.phone,
        };
    }

    // ========== Initial Load ==============================

    /**
     * Sets the page to a not found state:
     * - Hide the loading spinner
     * - Replace the main card with the loading skeleton
     */
    function notFound() {
        $pbLoadingSpinner.classList.add('d-none');
        $loadingSkeletonParent.classList.remove('d-none');
        $mainCard.classList.add('d-none');
    }

    /**
     * Loads the entire QRPT from the Hyejoo API.
     * Calls the hyejoo API to get the QRPT data, using the key or ID from the query string.
     * This should probably only be called once, as it will replace the entire page with the new data.
     * Other changes should call the API in the background and update the page as needed.
     */
    async function loadQRPT() {
        await axios.get<hyejoo.QuoteRequestProjectTasks>('/quote-request-project-tasks', {
            params: { 'q': q },
        }).then(resp => {
            if (resp.data.quoteRequestProjectTasks.length === 0) {
                notFound();
                HandleRequestError('No quote request project task found');
                return;
            }
            loadActivity(resp.data.quoteRequestProjectTasks[0]);
            $prepayBtn.addEventListener('click', async e => {
                e.preventDefault();
                await PrepayModal(Big(150), sess, key).then(() => {
                    new Modal({
                        body: template.Clone('#modal-quote-deposit-success') as HTMLElement,
                        cancelText: 'OK',
                        appendCancelButtonClass: 'btn-lg',
                        size: 'modal-sm',
                        onClose: () => {
                            window.location.reload();
                        },
                    });
                });
            });
            $cancelTaskBtn.addEventListener('click', e => {
                e.preventDefault();
                cancelModalAreYouSure();
            });
        }).catch(err => {
            notFound();
            HandleRequestError(err);
            return;
        });
        const cancelModalAreYouSure = () => {
            new Modal({
                title: 'Are you sure you want to cancel your project?',
                size: 'modal-sm',
                confirmText: 'Yes, Please Cancel',
                cancelText: "No, Don't Cancel",
                cancelButtonClass: 'btn btn-outline-secondary btn-lg col-12 col-md-auto flex-grow-1',
                confirmButtonClass: 'btn btn-secondary text-white btn-lg col-12 col-md-auto flex-grow-1',
                onSubmit: cancelModalForm,
            });
        };

        let reason = '';
        const cancelModalForm = () => {
            const modal = new Modal({
                title: 'We are sorry to hear you are canceling your project.',
                body: template.Clone('#modal-cancel-task-form') as HTMLElement,
                confirmText: 'Submit',
                cancelText: 'Close',
                cancelButtonClass: 'btn btn-outline-secondary btn-lg',
                confirmButtonClass: 'btn btn-secondary text-white btn-lg',
                onSubmit: () => {
                    submitCancel(reason);
                },
            });

            modal.Element.querySelector('.modal-confirm-btn')?.addEventListener('click', e => {
                $form.noValidate = true;
                const data = new FormData($form);
                if (ReportValidity($form)) {
                    if (data.get('cancel-reason') === 'other') {
                        reason = data.get('cancel-reason-other') as string;
                    } else {
                        reason = data.get('cancel-reason') as string;
                    }
                    submitCancel(reason);
                } else {
                    e.preventDefault();
                    return;
                }
            });

            const $form = modal.Element.querySelector('form') as HTMLFormElement;
            $form.querySelectorAll('input').forEach(el => {
                el.addEventListener('input', () => {
                    ReportValidity($form);
                    $form.dispatchEvent(new Event('clearinvalid'));
                    $form.querySelectorAll('.invalid-feedback').forEach(el => el.remove());
                    const data = new FormData($form);
                    const radio = data.get('cancel-reason') as string;
                    if (radio === 'other') {
                        document.querySelector('#cancel-reason-other')?.removeAttribute('disabled');
                        document.querySelector('#cancel-reason-other')?.setAttribute('required', '');
                        document.querySelector('#cancel-reason-other')?.classList.remove('d-none');
                    } else {
                        document.querySelector('#cancel-reason-other')?.setAttribute('disabled', '');
                        document.querySelector('#cancel-reason-other')?.classList.add('d-none');
                    }
                });
            });
        };

        const submitCancel = (reason: string) => {
            axios.delete(`/quote-request-project-tasks/${key}`, {
                params: { 'reason': reason },
            }).then(() => {
                toast.Success('Design ticket has been canceled.');
                window.location.reload();
            }).catch(err => {
                HandleRequestError(err);
            });
        };
    }

    /**
     * Takes a QRPT and loads it onto the page.
     * Starts by clearing the progress bar and activity items, re-cloning them, and then
     * adding the new data.
     * @param qrpt Quote Request Project Task data. Usually from the Hyejoo.
     */
    function loadActivity(qrpt: QuoteRequestProjectTask) {
        // Reset to initial state
        $pbDatesDesktop.forEach(el => el.classList.add('d-none'));
        $pbDatesMobile.forEach(el => el.classList.add('d-none'));
        $pbMilestones.forEach(el => el.classList.remove('pb-complete'));
        $pbMilestonesMobile.forEach(el => el.classList.add('d-none'));

        $pbDatesDesktop.forEach(el => el.innerText = 'Jan 1, 1970, 12:00 AM');
        $pbDatesMobile.forEach(el => el.innerText = 'Jan 1, 1970, 12:00 AM');

        $activityItems.innerHTML = '';
        $activityItems.append(template.Clone('#original-details-template'));

        $pbLoadingSpinner.classList.add('d-none');
        $loadingSkeletonParent.classList.add('d-none');
        $mainCard.classList.remove('d-none');

        const $originalDetails = document.querySelector('#original-details') as HTMLElement;
        const $originalDetailsBody = $originalDetails.querySelector('#original-details-body') as HTMLElement;
        const $originalDetailsProof = $originalDetails.querySelector('#original-details-proof') as HTMLElement;
        const $originalDetailsProofAnchor = $originalDetailsProof.querySelector('a') as HTMLAnchorElement;
        const $originalDetailsProofImage = $originalDetailsProof.querySelector('img') as HTMLImageElement;
        const $originalDetailsImages = $originalDetails.querySelector('#original-details-images') as HTMLElement;
        const $originalDetailsImagesGallery = $originalDetailsImages.querySelector('#original-details-image-gallery') as HTMLElement;

        // ========== Progress Bar ==============================

        const statusID = qrpt.quoteRequestProjectTaskStatusID ?? 0;

        let queueDate: DateTime | null = null;
        let progressDate: DateTime | null = null;
        let artDoneDate: DateTime | null = null;
        let sentDate: DateTime | null = null;

        if (qrpt.changelog && qrpt.changelog.length !== 0) {
            queueDate = DateTime.fromISO(qrpt.created ?? '');
            for (const log of qrpt.changelog) {
                if (log.column === 'QuoteRequestProjectTaskStatusID') {
                    switch (log.newValue) {
                    case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_IN_PROGRESS:
                        progressDate = DateTime.fromISO(log.time);
                        break;
                    case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_ART_DONE:
                        artDoneDate = DateTime.fromISO(log.time);
                        break;
                    case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_SENT:
                        sentDate = DateTime.fromISO(log.time);
                        break;
                    }
                }
            }
        }

        const drawProgress = (status: QuoteRequestProjectTaskStatusID | 0) => {
            switch (status) {
            case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_SENT:
                $pbSent.classList.add('pb-complete');
                $pbSentMobile.classList.remove('d-none');

                if (sentDate) {
                    $pbSentDate.classList.remove('d-none');
                    $pbSentDate.innerText = sentDate.toLocaleString(DateTime['DATETIME_MED']);

                    $pbSentDateMobile.classList.remove('d-none');
                    $pbSentDateMobile.innerText = sentDate.toLocaleString(DateTime['DATETIME_MED']);

                    const quoteLink = getFinishedQuoteLink(qrpt);
                    if (quoteLink) {
                        $pbCompleteViewQuoteBtn.href = quoteLink;
                    } // If undefined, button defaults to /quote
                }
            // falls through
            case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_ART_DONE:
                $pbArtDone.classList.add('pb-complete');
                $pbArtDoneMobile.classList.remove('d-none');

                $pbSentMobile.classList.add('d-none');
                $pbSentDateMobile.classList.add('d-none');

                if (artDoneDate) {
                    $pbArtDoneDate.classList.remove('d-none');
                    $pbArtDoneDate.innerText = artDoneDate.toLocaleString(DateTime['DATETIME_MED']);

                    $pbArtDoneDateMobile.classList.remove('d-none');
                    $pbArtDoneDateMobile.innerText = artDoneDate.toLocaleString(DateTime['DATETIME_MED']);
                }
            // falls through
            case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_PAUSED:
            case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_IN_PROGRESS:
                $pbProgress.classList.add('pb-complete');
                $pbProgressMobile.classList.remove('d-none');

                $pbArtDoneMobile.classList.add('d-none');
                $pbArtDoneDateMobile.classList.add('d-none');
                if (progressDate) {
                    $pbProgressDate.classList.remove('d-none');
                    $pbProgressDate.innerText = progressDate.toLocaleString(DateTime['DATETIME_MED']);

                    $pbProgressDateMobile.classList.remove('d-none');
                    $pbProgressDateMobile.innerText = progressDate.toLocaleString(DateTime['DATETIME_MED']);
                }
            // falls through
            default:
                $pbQueue.classList.add('pb-complete');
                $pbQueueMobile.classList.remove('d-none');

                $pbProgressMobile.classList.add('d-none');
                $pbProgressDateMobile.classList.add('d-none');

                if (queueDate) {
                    $pbQueueDate.classList.remove('d-none');
                    $pbQueueDate.innerText = queueDate.toLocaleString(DateTime['DATETIME_MED']);

                    $pbQueueDateMobile.classList.remove('d-none');
                    $pbQueueDateMobile.innerText = queueDate.toLocaleString(DateTime['DATETIME_MED']);
                }
            }
        };

        if (statusID !== QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_CUSTOMER_CANCELED) {
            drawProgress(statusID);
        } else {
            if (sentDate) {
                drawProgress(QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_SENT);
            } else if (artDoneDate) {
                drawProgress(QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_ART_DONE);
            } else if (progressDate) {
                drawProgress(QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_IN_PROGRESS);
            } else {
                drawProgress(0);
            }
        }

        // ========== Request Changes Form ==============================

        const newUploads = new uploads.Uploader($rcUploads, {
            confirmBtn: $rcSubmitBtn,
            thumbnails: $rcImageGallery,
            thumbnailMaxHeight: '5rem',
            thumbnailTemplate: $thumb,
            onidschange: () => {
                if (newUploads.GetUploadIDs().length !== 0) {
                    $rcUploadParent.classList.remove('d-none');
                    $rcFormTextArea.dispatchEvent(new Event('clearinvalid'));
                } else {
                    $rcUploadParent.classList.add('d-none');
                }
            },
        });

        $rcUploadButton.addEventListener('click', e => {
            e.preventDefault();
            $rcUploadInput.click();
        });

        $rcFormTextArea.addEventListener('keydown', e => {
            if (e.ctrlKey && e.key === 'Enter') {
                e.preventDefault();
                $rcSubmitBtn.click();
            }
        });

        $rcForm.noValidate = true;
        $rcForm.addEventListener('submit', async e => {
            e.preventDefault();

            let uploadParams: uploadParams;
            if (sessUser) {
                uploadParams = { 'user': sessUser };
            } else {
                HandleRequestError('You must be logged in to submit a change request.');
                return;
            }

            const inputText = $rcFormTextArea.value.trim();
            const newUploadList = newUploads.GetUploadIDs();
            const formElements = [$rcUploadButton, $rcSubmitBtn, $rcFormTextArea, $rcForm];
            const formDisable = () => formElements.forEach(el => el.setAttribute('disabled', ''));
            const formEnable = () => formElements.forEach(el => el.removeAttribute('disabled'));

            // Check if eiter the comment or an upload is present
            if (inputText.length === 0 && newUploadList.length === 0) {
                $rcFormTextArea.setCustomValidity('You must enter a comment or upload an image.');
            } else {
                $rcFormTextArea.setCustomValidity('');
            }

            if (!ReportValidity($rcForm)) {
                return;
            }

            formDisable();

            if (inputText.length === 0 && newUploadList.length === 0) {
                formEnable();
                return;
            }

            $rcLoadingSpinner.classList.remove('d-none');

            if (inputText) {
                uploadParams['Content'] = inputText;
            }
            if (newUploadList.length !== 0) {
                uploadParams['UploadIDs'] = newUploadList;
            }

            await axios.post<qrptPostResponse>(`quote-request-project-tasks/${key}/user-event`, uploadParams).then(resp => {
                $rcFormTextArea.value = '';
                formEnable();
                $rcLoadingSpinner.classList.add('d-none');
                $rcUploadParent.classList.add('d-none');

                const ue = resp.data.userEvent;
                const ueID = ue.id ?? '';

                const newActivityItem = loadUserEventActivityItem({
                    id: ueID,
                    content: inputText,
                    created: DateTime.local().toISO(),
                    userEventUploads: ue.userEventUploads,
                });

                undoToast('Comment submitted.', () => void deleteUserEvent(newActivityItem));
                newUploads.Clear();
                $activityItems.insertBefore(newActivityItem.element, $activityItems.firstChild);
            }).catch(err => {
                HandleRequestError(err);
                formEnable();
                $rcLoadingSpinner.classList.add('d-none');
            });
        });

        // ========== User Events / Activity Items ==============================

        const timeline: activityItem[] = [];
        const changelogColsToShow = ['QuoteRequestProjectTaskPriorityID', 'QuoteRequestProjectTaskStatusID'];
        if (qrpt.userEvent && qrpt.userEvent.length !== 0) {
            for (const ue of qrpt.userEvent) {
                timeline.push(loadUserEventActivityItem(ue));
            }
        }
        if (qrpt.changelog && qrpt.changelog.length !== 0) {
            for (const log of qrpt.changelog) {
                if (changelogColsToShow.includes(log.column)) {
                    const ai = loadChangelogActivityItem(log);
                    if (ai) {
                        timeline.push(ai);
                    }
                }
            }
        }
        timeline.sort((a, b) => b.createdTime.toMillis() - a.createdTime.toMillis()); // Sort by newest first
        for (const ai of timeline) {
            $activityItems.insertBefore(ai.element, $originalDetails);
        }
        if (changelogCancelDate && statusID === QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_CUSTOMER_CANCELED) {
            pbShowCanceled(changelogCancelDate);
        } else if (changelogSentDate) {
            pbShowSent(changelogSentDate);
        } else if (changelogPrepayDate) {
            pbShowPriority(changelogPrepayDate);
        }

        // ========== Original Details ==============================

        const qr = qrpt.quoteRequest as QuoteRequest;

        let details = '';
        const detailsJson = qr.notesObj as Record<string, string>;
        let detailFields = qr.notes?.split(/\n/);
        if (detailsJson) {
            const detailList = [];
            const detailEndLists = [];
            // A little bit of sorting since the JSON object is randomly ordered. These three should always be first.
            if (detailsJson['LineItem']) {
                detailList.push(`<b>Product:</b> ${Escape(detailsJson['LineItem'])}`);
                delete detailsJson['LineItem'];
            }
            if (detailsJson['Type']) {
                detailList.push(`<b>Type:</b> ${Escape(detailsJson['Type'])}`);
                delete detailsJson['Type'];
            }
            if (detailsJson['Size']) {
                detailList.push(`<b>Size:</b> ${Escape(detailsJson['Size'])}`);
                delete detailsJson['Size'];
            }
            // Now take out or generate the details that go at the end
            detailEndLists.push(`<b>Date Created:</b> ${DateTime.fromISO(qr.created ?? '').toLocaleString(DateTime['DATETIME_MED'])}`);
            if (qr.dateNeeded) {
                detailEndLists.push(`<b>Date Needed:</b> ${DateTime.fromISO(qr.dateNeeded).toLocaleString(DateTime['DATE_MED'])}`);
            }
            if (detailsJson['Quantity']) {
                detailEndLists.push(`Quantity:</b> ${Escape(detailsJson['Quantity'])}`);
                delete detailsJson['Quantity'];
            } else if (qr.quantity && qr.quantity !== 0) {
                detailEndLists.push(`<b>Quantity:</b> ${qr.quantity?.toLocaleString() ?? ''}`);
            }
            if (detailsJson['Budget']) {
                detailEndLists.push(`<b>Budget:</b> ${DollarFormat(detailsJson['Budget'])}`);
                delete detailsJson['Budget'];
            } else if (qr.budget && qr.budget !== '0') {
                detailEndLists.push(`<b>Budget:</b> ${DollarFormat(Escape(qr.budget))}`);
            }
            if (detailsJson['Notes']) {
                detailEndLists.push(`<b>Comments:</b><br>${Escape(detailsJson['Notes'])}`);
                delete detailsJson['Notes'];
            }
            for (const key in detailsJson) {
                if (Object.prototype.hasOwnProperty.call(detailsJson, key)) {
                    detailList.push(`<b>${Escape(key)}:</b> ${Escape(detailsJson[key])}`);
                }
            }
            detailList.push(...detailEndLists);
            const cleanDetails = detailList.map(line => {
                line = line.replace(/[\n\r]+/g, '\n');
                line = Nl2br(line);
                return `<p>${line}</p>`;
            });
            details += cleanDetails.join('');
        } else if (detailFields) {
            // find where the 'comment' section begins
            const firstEmptyLine = detailFields.indexOf('');

            // combine all lines in the 'comment' section into one line
            if (firstEmptyLine !== -1) {
                const comments = 'Comments: ' + detailFields.slice(firstEmptyLine + 1).join('\n');
                detailFields = detailFields.slice(0, firstEmptyLine);
                detailFields.push(Escape(comments));
            }

            // Manually add the date created, date needed, quantity, and budget fields
            detailFields = [
                ...detailFields.slice(0, detailFields.length - 1),
                `Date Created: ${DateTime.fromISO(qr.created ?? '').toLocaleString(DateTime['DATETIME_MED'])}`,
                qr.dateNeeded ? `Date Needed: ${DateTime.fromISO(qr.dateNeeded).toLocaleString(DateTime['DATE_MED'])}` : '',
                qr.quantity && qr.quantity !== 0 && !qr.notes?.includes('Quantity: ') ? `Quantity: ${qr.quantity?.toLocaleString() ?? ''}` : '',
                qr.budget && qr.budget !== '0' ? `Budget: ${DollarFormat(qr.budget)}` : '',
                detailFields[detailFields.length - 1],
            ];

            const lineCleanCheck = /^([^<>\r\n]+?):\s*(.*)/g;
            for (const line of detailFields) {
                if (line.length === 0) {
                    continue;
                }
                const match = lineCleanCheck.exec(line);
                if (match) {
                    if (match[1].includes('Comments')) {
                        details += Nl2br(`<p><b>${Escape(match[1])}:</b></p><p>${Escape(match[2])}</p>`);
                        continue;
                    } else {
                        details += Nl2br(`<p><b>${Escape(match[1])}:</b> ${Escape(match[2])}</p>`);
                    }
                }
                lineCleanCheck.lastIndex = 0;
            }
        }
        $originalDetailsBody.innerHTML = Nl2br(details);

        // Original Quote Request Images

        const imageURLs: string[] = [];

        if (qr.quoteRequestUploads && qr.quoteRequestUploads.length !== 0) {
            for (const qru of qr.quoteRequestUploads) {
                if (qru.upload) {
                    imageURLs.push(`${window.cloudFront}/${qru.upload.key ?? ''}`);
                }
            }
        }

        if (qr.quoteRequestImages && qr.quoteRequestImages.length !== 0) {
            for (const image of qr.quoteRequestImages) {
                imageURLs.push(`${window.cloudFront}/${image.image?.path ?? ''}/${image.image?.files?.Original?.File ?? ''}`);
            }
        }

        if (imageURLs.length !== 0) {
            $originalDetailsImages.classList.remove('d-none');
            imageURLs.forEach((url, i) => {
                const $galleryItem = template.Clone('#activity-item-image') as HTMLImageElement;
                const $galleryItemAnchor = $galleryItem.querySelector('a') as HTMLAnchorElement;
                const $galleryItemImg = $galleryItem.querySelector('img') as HTMLImageElement;
                $galleryItemAnchor.setAttribute('data-fancybox', 'original-details-images-gallery');
                $galleryItemAnchor.setAttribute('href', url);
                void uploads.Load($galleryItemImg, url, `image-${i + 1}`);
                $originalDetailsImagesGallery.appendChild($galleryItem);
            });
        }

        // Original Quote Request Proof

        if (qrpt.quoteProof) {
            const proof = qrpt.quoteProof.proof;
            const proofURLs = GetProofURLs(window.cloudFront, proof?.file ?? '', proof?.path ?? '');
            $originalDetailsProofAnchor.setAttribute('href', proofURLs['main']);
            $originalDetailsProofImage.setAttribute('src', proofURLs['thumb']);
            $originalDetailsProof.classList.remove('d-none');
        }
    } // End of loadActivity

    function pbShowPriority(time: DateTime) {
        $pbPriorityLine.classList.remove('d-none');
        $pbPriorityDate.innerText = time.toLocaleString(DateTime['DATE_SHORT']);
        $prepayBtn.remove();
    }

    function pbShowCanceled(time: DateTime) {
        $pbPriorityLine.classList.add('d-none');
        $pbcanceled.classList.remove('d-none');
        $pbCancelLine.classList.remove('d-none');
        $pbCanceledButtons.classList.remove('d-none');
        $pbCancelDate.innerText = time.toLocaleString(DateTime['DATE_SHORT']);
        $pbActionButtonParent.remove();
        $requestChanges.remove();
        document.querySelectorAll('.activity-item-btn-edit').forEach(el => el.remove());
        $pbCanceledReopenBtn.addEventListener('click', e => {
            e.preventDefault();
            reopenModal();
        });
    }

    function pbShowSent(time: DateTime) {
        $pbCompleteDate.innerText = time.toLocaleString(DateTime['DATE_SHORT']);
        $pbCompleteLine.classList.remove('d-none');
        $pbCompleteButtons.classList.remove('d-none');
        $pbPriorityLine.remove();
        $pbCanceledButtons.remove();
        $pbActionButtonParent.remove();
        $requestChanges.remove();
        document.querySelectorAll('.activity-item-btn-edit').forEach(el => el.remove());
    }

    function getFinishedQuoteLink(qrpt: QuoteRequestProjectTask) {
        let quoteSents: QuoteSent[] = [];
        if (qrpt.quoteRequest?.quotes && qrpt.quoteRequest.quotes.length !== 0) {
            for (const quote of qrpt.quoteRequest.quotes) {
                if (quote.quoteSents && quote.quoteSents.length !== 0) {
                    quoteSents = quoteSents.concat(quote.quoteSents);
                }
            }
            quoteSents = quoteSents.sort((a, b) => DateTime.fromISO(a.created ?? '').toMillis() - DateTime.fromISO(b.created ?? '').toMillis());
            const key = quoteSents[quoteSents.length - 1].key ?? '';
            return `${Relref('/quote')}?instance=${key}`;
        } else {
            return;
        }
    }

    const reopenModal = () => {
        new Modal({
            title: 'Would you like to reopen your project?',
            confirmText: 'Yes, Please Reopen',
            cancelText: "No, Don't Reopen",
            cancelButtonClass: 'btn btn-outline-secondary btn-lg col-12 col-md-auto flex-grow-1',
            confirmButtonClass: 'btn btn-secondary text-white btn-lg col-12 col-md-auto flex-grow-1',
            onSubmit: reopen,
        });
    };

    const reopen = () => {
        axios.patch<QuoteRequestProjectTask>(`/quote-request-project-tasks/${key}/reopen`).then(() => {
            toast.Success('Design ticket has been reopened.');
            window.location.reload();
        }).catch(err => {
            HandleRequestError(err);
        });
    };

    // ========== Activity Item Functions ==============================

    /**
     * Creates a new activityItem from a UserEvent.
     * @param ue User Event data.
     * @returns The new activity item as an HTMLElement.
     */
    function loadUserEventActivityItem(ue: UserEvent): activityItem {
        const $ue = template.Clone('#activity-item').firstElementChild as HTMLElement;
        const $ueDate = $ue.querySelector('.activity-item-date') as HTMLElement;
        const $ueContent = $ue.querySelector('.activity-item-content') as HTMLElement;
        const $ueContentHeading = $ue.querySelector('.activity-item-content-heading') as HTMLElement;
        const $ueButtons = $ue.querySelector('.activity-item-buttons') as HTMLElement;

        const ueID = ue.id ?? `activity-item-${UUID()}`;
        if (ue.id) {
            $ue.setAttribute('data-ue-id', ue.id);
        }

        const output: activityItem = {
            element: $ue,
            userEventID: ueID,
            uploads: [],
            deleteQueue: [],
            toasts: [],
            deletedUploads: [],
            content: ue.content ?? '',
            createdTime: DateTime.fromISO(ue.created ?? ''),
            updatedTime: ue.updated ? DateTime.fromISO(ue.updated) : undefined,
            deleted: false,
        };

        if (ue.created) {
            $ueDate.innerHTML = DateTime.fromISO(ue.created).toLocaleString(DateTime['DATE_SHORT']);
            if (ue.updated) {
                const updatedDate = DateTime.fromISO(ue.updated).toLocaleString(DateTime['DATE_SHORT']);
                const separatorChar = '\u2022'; // Unicode bullet character
                $ueDate.innerHTML += `  ${separatorChar}  Edited on ${updatedDate}`;
            }
            $ueDate.classList.remove('d-none');
        }

        if (ue.content) {
            $ueContentHeading.classList.remove('d-none');
            $ueContent.classList.remove('d-none');
            $ueContent.innerHTML = Nl2br(Escape(ue.content));
        }

        const $ueUploads = template.Clone('#uploads-section') as HTMLElement;
        const $ueUploadsGalleryParent = $ueUploads.querySelector('.activity-item-files') as HTMLElement;
        const $ueUploadsGallery = $ueUploads.querySelector('.activity-item-image-gallery') as HTMLElement;
        $ue.insertBefore($ueUploads, $ueContent.nextSibling);
        if (ue.userEventUploads && ue.userEventUploads.length !== 0) {
            $ueUploadsGalleryParent.classList.remove('d-none');
            for (const upload of ue.userEventUploads) {
                const $upload = template.Clone('#activity-item-image') as HTMLElement;
                const $uploadParent = $upload.querySelector('.activity-item-image-parent') as HTMLElement;
                const $uploadAnchor = $upload.querySelector('a') as HTMLAnchorElement;
                const $uploadImg = $upload.querySelector('img') as HTMLImageElement;
                const $uploadDeleteBtn = $upload.querySelector('.upload-thumbnail-close') as HTMLElement;

                const uploadItem: activityItemUpload = {
                    userEventUpload: upload,
                    element: $uploadParent,
                };

                output.uploads.push(uploadItem);

                $uploadAnchor.setAttribute('data-fancybox', ueID);
                $uploadAnchor.setAttribute('href', `${window.cloudFront}/${upload.upload?.key ?? ''}`);
                void uploads.Load($uploadImg, `${window.cloudFront}/${upload.upload?.key ?? ''}`, `${upload.upload?.name ?? ue.userEventUploads.indexOf(upload) + 1}`);
                $uploadDeleteBtn.addEventListener('click', e => {
                    e.preventDefault();
                    queueDeleteUserEventUpload(output, uploadItem);
                });
                $ueUploadsGallery.appendChild($upload);
            }
        }

        $ueButtons.querySelector('.activity-item-btn-edit')?.addEventListener('click', e => {
            e.preventDefault();
            activityItemEdit(output);
        });

        return output;
    }

    /**
     * Creates a new activityItem from a changelog event.
     * @param ue User Event data.
     * @returns The new activity item as an HTMLElement.
     */
    function loadChangelogActivityItem(log: ModelChangelogEntry): activityItem | undefined {
        const $ai = template.Clone('#activity-item').firstElementChild as HTMLElement;
        const $aiDate = $ai.querySelector('.activity-item-date') as HTMLElement;
        const $aiContent = $ai.querySelector('.activity-item-content') as HTMLElement;
        const $aiContentHeading = $ai.querySelector('.activity-item-content-heading') as HTMLElement;
        const $aiButtons = $ai.querySelector('.activity-item-buttons') as HTMLElement;
        const $aiEditButtons = $ai.querySelector('.activity-item-edit-btns') as HTMLElement;

        $aiContentHeading.classList.add('d-none');
        $aiButtons.classList.add('d-none');
        $aiEditButtons.classList.add('d-none');

        $aiDate.innerHTML = DateTime.fromISO(log.time).toLocaleString(DateTime['DATE_SHORT']);
        $aiDate.classList.remove('d-none');

        let content: string = '';
        let message: string = '';
        const iconMilestone: string = '<div class="icon-milestone"></div>';
        const iconPriority: string = '<div class="icon-priority"></div>';
        const iconCanceled: string = '<div class="icon-canceled"></div>';
        const iconReopened: string = '<div class="icon-reopened"></div>';

        if (log.column === 'QuoteRequestProjectTaskPriorityID' && log.newValue === QuoteRequestProjectTaskPriorityID.QUOTE_REQUEST_PROJECT_TASK_PRIORITIES_PREPAID) {
            message = 'Deposit Received and Design Ticket <b class="text-success">PRIORITIZED</b>';
            content = `${iconPriority} <div>${message}</div>`;
            if (!changelogPrepayDate || DateTime.fromISO(log.time) > changelogPrepayDate) {
                changelogPrepayDate = DateTime.fromISO(log.time);
            }
        } else if (log.column === 'QuoteRequestProjectTaskStatusID') {
            if (log.oldValue === QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_CUSTOMER_CANCELED) {
                message = 'Customer <b class="text-success">REOPENED</b> quote request. ';
                switch (log.newValue) {
                case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_IN_PROGRESS:
                    message += 'Artwork is in progress.';
                    break;
                case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_ART_DONE:
                    message += 'Artwork is completed. Waiting for pricing.';
                    break;
                default:
                    message += 'Design ticket is queued.';
                }
                content = `${iconReopened} <div>${message}</div>`;
            } else {
                switch (log.newValue) {
                case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_IN_PROGRESS:
                    message = 'Artwork is in progress.';
                    content = `${iconMilestone} <div>${message}</div>`;
                    break;
                case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_ART_DONE:
                    message = 'Artwork completed.';
                    content = `${iconMilestone} <div>${message}</div>`;
                    break;
                case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_SENT:
                    message = 'Quote completed and sent to customer.';
                    content = `${iconMilestone} <div>${message}</div>`;
                    if (!changelogSentDate || DateTime.fromISO(log.time) > changelogSentDate) {
                        changelogSentDate = DateTime.fromISO(log.time);
                    }
                    break;
                case QuoteRequestProjectTaskStatusID.QUOTE_REQUEST_PROJECT_TASK_STATUSES_CUSTOMER_CANCELED:
                    message = 'Customer <b class="text-danger">CANCELED</b> quote request.';
                    content = `${iconCanceled} <div>${message}</div>`;
                    if (!changelogCancelDate || DateTime.fromISO(log.time) > changelogCancelDate) {
                        changelogCancelDate = DateTime.fromISO(log.time);
                    }
                    break;
                default:
                    return undefined;
                }
            }
        } else {
            return undefined;
        }

        $aiContent.classList.remove('d-none', 'mb-4');
        $aiContent.classList.add('d-flex', 'align-items-center', 'gap-2');
        $aiContent.innerHTML = content;

        const output: activityItem = {
            element: $ai,
            userEventID: undefined,
            uploads: [],
            deleteQueue: [],
            toasts: [],
            deletedUploads: [],
            content: '',
            createdTime: DateTime.fromISO(log.time),
            updatedTime: undefined,
            deleted: false,
        };

        return output;
    }

    /**
     * Puts an activity item into edit mode. This will replace the content with a textarea
     * and add buttons for submitting, canceling, and deleting the comment.
     * Eventually, this will also allow for uploading images, but that isn't implemented in
     * the Hyejoo yet.
     * @param ue The activity item to edit.
     */
    function activityItemEdit(ai: activityItem) {
        const $ue = ai.element;

        const $ueContent = $ue.querySelector('.activity-item-content') as HTMLElement;
        const $ueUploads = $ue.querySelector('.activity-item-files') as HTMLElement;
        const $ueUploadsGallery = $ue.querySelector('.activity-item-image-gallery') as HTMLElement;
        const $ueButtons = $ue.querySelector('.activity-item-buttons') as HTMLElement;
        const $ueUploadDeleteButtons = $ue.querySelectorAll('.upload-thumbnail-close') as NodeListOf<HTMLElement>;

        const content = $ueContent.innerText;

        $ueUploadsGallery.classList.remove('gap-2');
        $ueUploadsGallery.classList.add('gap-5');

        $ueUploadDeleteButtons.forEach($btn => $btn.classList.remove('d-none'));

        const $form = document.createElement('form');
        $form.id = $ue.getAttribute('data-ue-id') ?? `activity-item-edit-${UUID()}`;

        const $textarea = document.createElement('textarea');
        $textarea.setAttribute('form', $form.id);
        $textarea.classList.add('form-control', 'mb-4');
        $textarea.rows = 3;
        $textarea.value = content;
        $form.appendChild($textarea);

        const $editButtons = $ue.querySelector('.activity-item-edit-btns') as HTMLElement;
        $editButtons.classList.remove('d-none');

        const $submit = $ue.querySelector('.activity-item-edit-submit') as HTMLButtonElement;
        $submit.setAttribute('form', $form.id);

        const $cancel = $ue.querySelector('.activity-item-edit-cancel') as HTMLButtonElement;
        $cancel.setAttribute('form', $form.id);

        const $delete = $ue.querySelector('.activity-item-edit-delete') as HTMLButtonElement;
        $delete.setAttribute('form', $form.id);

        const $uploadLabel = $editButtons.querySelector('label') as HTMLLabelElement;
        const $uploadBtn = $uploadLabel.querySelector('.activity-item-browse-files') as HTMLButtonElement;
        const $uploadInput = $uploadLabel.querySelector('input') as HTMLInputElement;
        const $uploader = new uploads.Uploader($uploadLabel, {
            confirmBtn: $submit,
            thumbnails: $ueUploadsGallery,
            thumbnailMaxHeight: '5rem',
            thumbnailTemplate: $thumb,
            onidschange: () => {
                if ($uploader.GetUploadIDs().length !== 0) {
                    $ueUploadsGallery.classList.remove('d-none');
                    $ueUploads.classList.remove('d-none');
                } else {
                    $ueUploadsGallery.classList.add('d-none');
                    $ueUploads.classList.add('d-none');
                }
            },
        });

        $uploadBtn.addEventListener('click', e => {
            e.preventDefault();
            $uploadInput.click();
        });

        $cancel.addEventListener('click', e => {
            e.preventDefault();
            ai.deleteQueue.forEach(deleteQueueItem => {
                queueRestoreUserEventUpload(ai, deleteQueueItem, true);
            });
            ai.deleteQueue = [];
            ai.toasts.forEach(t => {
                try {
                    t?.dispose();
                } catch {
                    // Do nothing
                }
            });
            ai.toasts = [];
            close();
            return;
        });

        $delete.addEventListener('click', e => {
            e.preventDefault();
            close();
            void deleteUserEvent(ai);
            return;
        });

        $ueContent.classList.add('d-none');
        $ueButtons.classList.add('d-none');

        $ue.insertBefore($form, $ueContent.nextSibling);
        $ue.appendChild($editButtons);

        $form.addEventListener('submit', async e => {
            e.preventDefault();
            const newUploads = new Set(ai.uploads.map(upload => upload.userEventUpload.uploadID));
            const deletedUploads = new Set(ai.deletedUploads.map(upload => upload.userEventUpload.uploadID));
            for (const upload of newUploads) {
                if (deletedUploads.has(upload)) {
                    newUploads.delete(upload);
                }
                if (ai.deleteQueue.some(del => del.userEventUpload.uploadID === upload)) {
                    newUploads.delete(upload);
                }
            }
            $uploader.GetUploadIDs().forEach(id => newUploads.add(id));
            $uploader.Clear();
            await updateActivityItem(ai, $textarea.value.trim(), Array.from(newUploads)).then(close);
            return;
        });

        const close = () => {
            $ueContent.classList.remove('d-none');
            $ueButtons.classList.remove('d-none');
            $uploader.Clear();
            $form.remove();
            $editButtons.classList.add('d-none');
            $ueUploadsGallery.classList.remove('gap-5');
            $ueUploadsGallery.classList.add('gap-2');
            $ueUploadDeleteButtons.forEach($btn => $btn.classList.add('d-none'));
        };
    }

    /**
     * Updates the content and uploads of a user event. If there are queued deletions, they will be processed.
     * @param ai The activity item to update.
     * @param newContent The new text of the user event.
     */
    async function updateActivityItem(ai: activityItem, newContent: string, newUploads?: (number | undefined)[], showToast = true) {
        const $spinner = ai.element.querySelector('.overlay') as HTMLElement;
        $spinner.classList.remove('d-none');

        // if this update will end up with no content and no uploads, just delete the activity item.
        if (((ai.uploads.length === (ai.deletedUploads.length + ai.deleteQueue.length) && ai.uploads.length > 0) || newUploads?.length === 0) && newContent.length === 0) {
            void deleteUserEvent(ai).then(() => $spinner.classList.add('d-none'));
            return;
        }

        ai.toasts.forEach(t => {
            try {
                t?.dispose();
            } catch {
                // Do nothing
            }
        });
        ai.toasts = [];

        const originalAI = {
            content: ai.content,
            uploads: ai.uploads,
        };

        if (!newUploads) {
            newUploads = ai.uploads.map(upload => upload.userEventUpload.uploadID);
            newUploads = newUploads.filter(id => !ai.deletedUploads.some(upload => upload.userEventUpload.uploadID === id));
            newUploads = newUploads.filter(id => !ai.deleteQueue.some(upload => upload.userEventUpload.uploadID === id));
        }

        await axios.patch<uePatchResponse>(`quote-request-project-tasks/${key}/user-event/${ai.userEventID ?? ''}`, {
            'content': newContent ?? '',
            'uploadIDs': newUploads,
            'user': sessUser,
        }).then(resp => {
            const updatedAI = loadUserEventActivityItem(resp.data.userEvent);
            $activityItems.insertBefore(updatedAI.element, ai.element);
            ai.element.remove();
            ai = updatedAI;
        }).catch(err => {
            HandleRequestError(err);
        }).finally(() => {
            $spinner.classList.add('d-none');
            if (((ai.uploads.length === ai.deletedUploads.length && ai.uploads.length > 0) || ai.uploads.length === 0) && ai.content.length === 0) {
                ai.element.classList.add('d-none');
                ai.deleted = true;
            }
        });

        if (showToast) {
            ai.toasts.push(undoToast('Comment updated.', () => updateActivityItem(ai, originalAI.content, originalAI.uploads.map(upload => upload.userEventUpload.uploadID))));
        }

        return ai;
    }

    /**
     * Deletes a user event from the QRPT.
     * @param ai The activity item to delete.
     */
    async function deleteUserEvent(ai: activityItem, showToast = true) {
        if (ai.deleted) {
            return;
        }
        ai.deleted = true;
        ai.element.classList.add('d-none');
        await axios.delete(`quote-request-project-tasks/${key}/user-event/${ai.userEventID ?? ''}`).then(() => {
            if (showToast) {
                ai.toasts.push(undoToast('Comment deleted.', async () => await restoreUserEvent(ai, showToast)));
            }
        }).catch(err => {
            HandleRequestError(err);
        });
    }

    /**
     * Restores a user event that was previously deleted.
     * @param ai The activity item to restore.
     */
    async function restoreUserEvent(ai: activityItem, showToast = true) {
        if (!ai.deleted) {
            return;
        }
        ai.deleted = false;
        ai.element.classList.remove('d-none');
        await axios.patch(`quote-request-project-tasks/${key}/user-event/${ai.userEventID ?? ''}/active`,
            { 'active': true },
        ).then(async () => {
            const uploads = ai.uploads.filter(upload => !ai.deletedUploads.includes(upload));
            uploads.push(...ai.deleteQueue);
            const newAI = await updateActivityItem(ai, ai.content, uploads.map(upload => upload.userEventUpload.uploadID), false) as activityItem;
            if (showToast) {
                ai.toasts.push(undoToast('Comment restored.', () => deleteUserEvent(newAI, showToast)));
            }
        }).catch(err => {
            HandleRequestError(err);
        });
    }

    /**
     * Queues a UserEventUpload to be deleted. This should look like the upload was deleted,
     * but not commit it until the activity item edit is submitted.
    * @param upload The upload to delete.
    * @param ai The activity item that the upload belongs to.
     */
    function queueDeleteUserEventUpload(ai: activityItem, upload: activityItemUpload, showToast = true): void {
        if (!ai.deleteQueue.includes(upload)) {
            ai.deleteQueue.push(upload);
        }
        upload.element.classList.add('d-none');
        if (ai.uploads.length === ai.deleteQueue.length) {
            ai.element.querySelector('.activity-item-files')?.classList.add('d-none');
        }
        if (showToast) {
            ai.toasts.push(undoToast('Image deleted.', () => queueRestoreUserEventUpload(ai, upload, showToast)));
        }
    }

    /**
     * Queues a UserEventUpload to be restored. This should look like the upload was restored,
     * but not commit it until the activity item edit is submitted.
     * @param upload The upload to restore.
     * @param ai The activity item that the upload belongs to.
     */
    function queueRestoreUserEventUpload(ai: activityItem, upload: activityItemUpload, showToast = true): void {
        ai.deleteQueue = ai.deleteQueue.filter(item => item !== upload);
        if (showToast) {
            ai.toasts.push(undoToast('Image restored.', () => queueDeleteUserEventUpload(ai, upload, showToast)));
        }
        upload.element.classList.remove('d-none');
    }

    /**
     * Displays a success toast with an undo link.
     * @param message The success message to display.
     * @param undoCallback The callback function to execute when the undo link is clicked.
     */
    function undoToast(message: string, undoCallback: () => void): Toast | undefined {
        const undoLinkID = `undo-${UUID()}`;
        const t = toast.Success(`${message} <a href="${Relref('/')}" class="text-white text-decoration-underline" id="${undoLinkID}">Undo</a>`);
        const undo = document.querySelector(`#${undoLinkID}`) as HTMLAnchorElement;
        undo.addEventListener('click', e => {
            e.preventDefault();
            undoCallback();
            t?.dispose();
        });
        return t;
    }

    await loadQRPT().then(
        () => {
            const params = new URLSearchParams(window.location.search);
            if (params.has('prepay')) {
                $prepayBtn.click();
            } else if (params.has('cancel')) {
                $cancelTaskBtn.click();
            } else {
                $rcFormTextArea.focus();
            }
        },
    );
}